import { useState } from "react";
import { motion } from "framer-motion";
import { Check, MapPin, Search, BadgeCheck } from "lucide-react";
import { Reveal, SectionHead } from "@/components/Reveal";
import { SAMPLE_MEMBERS } from "@/data/content";

const CITIES = ["Tous", "Avignon", "Orange", "Nîmes", "Cavaillon"];
const BULLETS = [
  "Recherche par métier et compétences",
  "Recherche par ville et localisation",
  "Profils professionnels complets",
  "Possibilité de contacter les membres",
  "Offres exclusives entre membres",
];

export default function Reseau() {
  const [city, setCity] = useState("Tous");
  const filtered = city === "Tous" ? SAMPLE_MEMBERS : SAMPLE_MEMBERS.filter((m) => m.city === city);

  return (
    <section id="reseau" className="relative overflow-hidden bg-night-soft py-16 md:py-24 lg:py-32" data-testid="reseau-section">
      <div className="mx-auto grid max-w-7xl items-center gap-14 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
        <div>
          <SectionHead
            eyebrow="Un réseau qui vous ressemble"
            title="L'annuaire professionnel du club."
            sub="Chaque membre est présenté avec son métier, ses compétences et sa ville. Trouvez le bon contact en quelques secondes, où que vous soyez dans le Sud-Est."
          />
          <Reveal delay={0.15}>
            <ul className="mt-8 space-y-3">
              {BULLETS.map((b) => (
                <li key={b} className="flex items-center gap-3 text-sm text-foreground/90 sm:text-base">
                  <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-gold/40 bg-gold/10">
                    <Check className="h-3.5 w-3.5 text-gold" />
                  </span>
                  {b}
                </li>
              ))}
            </ul>
            <blockquote className="mt-10 rounded-2xl border border-gold/30 bg-gold/5 p-6" data-testid="reseau-quote">
              <p className="font-serif text-xl italic leading-snug text-foreground sm:text-2xl">
                "Votre prochaine rencontre peut devenir votre prochain client, partenaire ou associé."
              </p>
            </blockquote>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <div className="rounded-3xl border border-border bg-card p-6 sm:p-8" data-testid="reseau-demo-panel">
            <div className="flex items-center gap-2.5">
              <Search className="h-4 w-4 text-gold" />
              <span className="eyebrow">Démonstration de l'annuaire</span>
            </div>
            <div className="mt-5 flex flex-wrap gap-2" data-testid="reseau-filter-chips">
              {CITIES.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setCity(c)}
                  data-testid={`reseau-chip-${c.toLowerCase()}`}
                  className={`rounded-full px-4 py-1.5 text-xs font-semibold uppercase tracking-wider transition-all duration-300 ${
                    city === c ? "bg-gold text-night" : "border border-gold/30 text-gold hover:bg-gold/10"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
            <div className="mt-6 space-y-3" data-testid="reseau-member-list">
              {filtered.map((m, i) => (
                <motion.div
                  key={`${m.city}-${i}`}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="flex items-center gap-4 rounded-xl border border-border bg-background p-3.5 transition-colors duration-300 hover:border-gold/40"
                >
                  <img src={m.img} alt="" className="h-12 w-12 rounded-full border-2 border-gold/40 object-cover" />
                  <div className="min-w-0 flex-1">
                    <p className="flex items-center gap-1.5 text-sm font-bold text-foreground">
                      {m.name}
                      <BadgeCheck className="h-3.5 w-3.5 shrink-0 text-electric" />
                    </p>
                    <p className="truncate text-xs text-muted-foreground">{m.job}</p>
                  </div>
                  <span className="flex shrink-0 items-center gap-1 rounded-full bg-gold/10 px-2.5 py-1 text-[11px] font-semibold text-gold">
                    <MapPin className="h-3 w-3" /> {m.city}
                  </span>
                </motion.div>
              ))}
              {filtered.length === 0 && (
                <p className="py-6 text-center text-sm text-muted-foreground">Aucun profil d'exemple pour cette ville.</p>
              )}
            </div>
            <p className="mt-5 flex items-center gap-2 text-[11px] text-muted-foreground">
              <span className="rounded border border-border px-1.5 py-0.5 font-mono text-[10px] uppercase">Exemple</span>
              Profils fictifs — remplacés par les vrais membres dans l'application.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
